import { jsonToHtml } from '@contentstack/json-rte-serializer'
import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import { useMemo } from 'react'

import {
  DynamicVariable,
  ExternalLink,
  Field,
  FieldsFragment,
  JsonField,
  JSONType,
  Link,
  LinkField,
  Maybe,
  MultipleLinkField,
  ObjectField,
  Page,
  ProductField,
  Reference,
  ReferenceField,
  RteField,
  Scalars,
  Wordbook,
} from '@Generated/graphql'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'

type NavigationField = Extract<FieldsFragment, { __typename: 'NavigationField' }>
type NavigationItem = NonNullable<NavigationField['navigation']>[0]

const isDynamicVariable = (reference: Reference | undefined): reference is DynamicVariable => reference?.__typename === 'DynamicVariable'
const isJSONField = (field: Field | undefined): field is JsonField => field?.__typename === 'JSONField'
const isObjectField = (field: Field | undefined): field is ObjectField => field?.__typename === 'ObjectField'
const isReferenceField = (field: Field | undefined): field is ReferenceField => field?.__typename === 'ReferenceField'
const isMultipleLinkField = (field: Field | undefined): field is MultipleLinkField => field?.__typename === 'MultipleLinkField'
const isNavigationield = (field: Field | undefined): field is NavigationField => field?.__typename === 'NavigationField'
const isLinkField = (field: Field | undefined): field is LinkField => field?.__typename === 'LinkField'
const isProductField = (field: Field | undefined): field is ProductField => field?.__typename === 'ProductField'
const isExternalLinkReference = (reference: Reference | undefined): reference is ExternalLink => reference?.__typename === 'ExternalLink'
const isPageReference = (reference: Reference | undefined): reference is Page => reference?.__typename === 'Page'
const isWordbook = (reference: Reference | undefined): reference is Wordbook => reference?.__typename === 'Wordbook'
const isRTEField = (field: Field | undefined): field is RteField => field?.__typename === 'RTEField'

export const getRTEFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isRTEField(field) ? field : null
}

const resolveRTEReference = <T extends Scalars['JSON']>(input: T, references: Record<string, Reference>): T => {
  if (!input) {
    return input
  }

  if (isArray(input)) {
    return map(input, item => resolveRTEReference(item, references)) as T
  }

  if (!isObject(input)) {
    return input
  }

  // We have to clone input as it is readonly on server side
  const result = { ...(input as any) }

  if (input.children) {
    result.children = resolveRTEReference(input.children, references)
  }

  if (!input.referenceKey) {
    return result
  }

  const reference = references[input.referenceKey as string]

  if (isDynamicVariable(reference)) {
    result.children = [{ text: reference.value }]

    if (reference.url) {
      result.children = [
        {
          attrs: {
            href: reference.url,
          },
          children: result.children,
          type: 'a',
        },
      ]
    }
  }

  if (isExternalLinkReference(reference) || isPageReference(reference)) {
    // TODO: remove when graph-api doesn't return HTML for ExternalLink anymore
    if (includes(reference.url, '<')) {
      console.warn('url contains html characters', reference.url)
      return result
    }

    if (!result.attrs) {
      result.attrs = {}
    }

    if (isPageReference(reference) && reference.type === 'modals') {
      result.attrs = {
        ...result.attrs,
        'data-modal-id': reference.id,
        href: '#',
      }

      return result
    }

    // We have to clone attrs as they are readonly on server side
    result.attrs = {
      ...result.attrs,
      href: reference.url,
    }
  }

  return result
}

export const convertRTEToHTML = (data: RteField | null | undefined, dynamicVariables: Record<string, string> = {}) => {
  if (!data || !data.value) {
    return ''
  }

  let result: any = data.value
  if (data.referencesWithKey && data.referencesWithKey.length !== 0) {
    const references = reduce(data.referencesWithKey, (memo, referenceWithKey) => {
      if (!referenceWithKey || !referenceWithKey.key || !referenceWithKey.reference) {
        return memo
      }

      // eslint-disable-next-line no-param-reassign
      memo[referenceWithKey.key] = referenceWithKey.reference

      if (isDynamicVariable(referenceWithKey.reference) && referenceWithKey.reference?.id && dynamicVariables[referenceWithKey.reference.id]) {
        // eslint-disable-next-line no-param-reassign
        memo[referenceWithKey.key] = {
          ...referenceWithKey.reference,
          value: dynamicVariables[referenceWithKey.reference.id],
        }
      }
      return memo
    }, {})
    result = resolveRTEReference(data.value, references)
  }

  // When the first element is an empty p-tag, there is no reason to render anything
  if (
    result
    && result.type === 'doc'
    && result.children?.length === 1
    && result.children[0]?.type === 'p'
    && result.children[0]?.children?.length === 1
    && result.children[0].children[0]?.text === ''
  ) {
    return ''
  }

  return jsonToHtml(result)
}

export const getRTEFieldText = (fields: Field[], key: string) => {
  const data = getRTEFieldValue(fields, key)
  return convertRTEToHTML(data)
}

export const getJSONFieldValue = <T extends Scalars['JSON']>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isJSONField(field) ? field.value as T : null
}

export const getObjectFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isObjectField(field) ? (field.fields || []) : null
}

export const getReferenceFieldValue = <T extends Reference>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isReferenceField(field) ? (field.references || []) as T[] : null
}

export const getMultipleLinkFieldValue = <T extends Link>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isMultipleLinkField(field) ? (field.links || []) as T[] : null
}

export const getNavigationFieldItems = <T extends NavigationItem>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isNavigationield(field) ? (field.navigation || []) as T[] : null
}

export const getLinkFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isLinkField(field) ? field : null
}

export const getLinkType = (link: LinkField) => {
  const reference = first(link?.references)
  if (isPageReference(reference)) {
    return reference.type
  }
  if (isExternalLinkReference(reference)) {
    return 'external'
  }
  return reference?.__typename || ''
}

export const getProductFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  if (isProductField(field) && field.product) {
    return field.product
  }
  return null
}

export const getProductReferenceValue = (fields: Field[], key: string) => {
  const referenceField = getReferenceFieldValue<Page>(fields, key)

  if (!referenceField) {
    return []
  }

  return compact(
    map(
      referenceField,
      x => getProductFieldValue(x.fields || [], 'product'),
    ),
  )
}

export const getPageReferences = (references: Reference[]): Page[] => filter(references, isPageReference)

export function useRTEField(fields: any[] | null | undefined, key: string) {
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()

  return useMemo(() => {
    const field = getRTEFieldValue(fields || [], key)
    if (!field) {
      return null
    }

    const dynamicVariables = {}

    if (cheapestShippingPrice?.format) {
      dynamicVariables['store.cheapest_shipping_price'] = cheapestShippingPrice.format
    }

    if (freeShippingThreshold?.format) {
      dynamicVariables['store.free_shipping_threshold'] = freeShippingThreshold.format
    }

    return convertRTEToHTML(field, dynamicVariables)
  }, [fields, cheapestShippingPrice?.format, freeShippingThreshold?.format, key])
}

export function parseExtraColors<
  const C extends readonly string[],
  R = { [K in C[number]]: string },
>(input: Maybe<JSONType> | undefined, colors: C): R {
  const obj = isObject(input) ? input : {}
  const result = {} as R

  colors.forEach((color) => {
    const value = obj[color]
    result[color] = value && isString(value) ? value : 'transparent'
  })

  return result
}

export const getLinkData = (link: Maybe<LinkField> | undefined) => {
  const reference = first(link?.references)

  let title = ''
  let type = ''

  if (isPageReference(reference)) {
    title = reference.title || ''
    type = reference.type
  } else if (isExternalLinkReference(reference)) {
    type = 'external'
  } else if (reference?.__typename) {
    type = reference.__typename
  }

  return {
    id: reference?.id || '',
    title,
    type,
  }
}

export const getLinkFromReference = (reference: any | null | undefined) => {
  if (!reference) {
    return null
  }

  let text = ''
  let type = ''
  let url = ''

  if (isPageReference(reference)) {
    text = reference.title || ''
    type = reference.type
    url = reference.url || ''
  } else if (isExternalLinkReference(reference)) {
    type = 'external'
    url = reference.url || ''
  } else if (isWordbook(reference)) {
    type = 'wordbook'
    text = reference.value || ''
  } else if (reference?.__typename) {
    type = reference.__typename
  }

  if (!type) {
    return null
  }

  return {
    id: reference?.id || '',
    text,
    type,
    url,
  }
}
